import { FC, useCallback, useEffect, useState } from "react";
import { Drawer, LinearProgress, Stack } from "@mui/material";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { BillingExplorerSavingOpportunitiesDrawerHeader } from "@/components/pages/resource-explorer/components/resource-explorer-card/components/resource-explorer-metadata/components/saving-opportunities/drawer/BillingExplorerSavingOpportunitiesDrawerHeader";
import { BillingExplorerSavingOpportunitiesDrawerBody } from "@/components/pages/resource-explorer/components/resource-explorer-card/components/resource-explorer-metadata/components/saving-opportunities/drawer/BillingExplorerSavingOpportunitiesDrawerBody";
import { initiateSavingsOpportunitiesThunk } from "@/store/savings-opportunities/thunks/initiateSavingsOpportunitiesThunk";
import { usePanelHorizontalResize } from "@/components/utils/hooks/usePanelHorizontalResize";
import { ResourceDetailsDragHandler } from "@/components/pages/common/resource-details/components/ResourceDetailsDragHandler";
import { savingsOpportunitiesLoadingSelector } from "@/store/savings-opportunities/selectors/savingsOpportunitiesLoadingSelector";
import { savingsOpportunitiesViewsHierarchyLoadingSelector } from "@/store/savings-opportunities/selectors/views-hierarchy/loadings/savingsOpportunitiesViewsHierarchyLoadingSelector";
import { resourceExplorerFilterTreeSelector } from "@/store/resource-explorer/selectors/filter-tree/resourceExplorerFilterTreeSelector";

interface BillingExplorerSavingOpportunitiesDrawerProps {
  open: boolean;
  onClose(): void;
}

export const BillingExplorerSavingOpportunitiesDrawer: FC<
  BillingExplorerSavingOpportunitiesDrawerProps
> = ({ open, onClose }) => {
  const dispatch = useAppDispatch();
  const filterTree = useAppSelector(resourceExplorerFilterTreeSelector);
  const opportunitiesLoading = useAppSelector(
    savingsOpportunitiesLoadingSelector,
  );
  const viewsLoading = useAppSelector(
    savingsOpportunitiesViewsHierarchyLoadingSelector,
  );
  const { width, handleMouseDown } = usePanelHorizontalResize(
    resizingDefaultParams,
  );
  const [renderBody, setRenderBody] = useState(false);
  const onTransitionEnd = useCallback(() => {
    !renderBody && open && setRenderBody(true);
  }, [renderBody, open]);
  const loading = opportunitiesLoading || viewsLoading;
  const drawerCloseHandler = useCallback(() => {
    setRenderBody(false);
    onClose();
  }, [onClose]);

  useEffect(() => {
    if (open) {
      dispatch(initiateSavingsOpportunitiesThunk(filterTree));
    }
  }, [open, dispatch, filterTree]);

  return (
    <Drawer
      anchor="right"
      onClose={drawerCloseHandler}
      open={open}
      onTransitionEnd={onTransitionEnd}
      PaperProps={{
        sx: { width, height: "100vh", overflow: "hidden", ...paperProps },
      }}
    >
      <Stack flex={1} height="100%">
        <Stack direction="row" flex={1} overflow="hidden">
          <ResourceDetailsDragHandler onMouseDown={handleMouseDown} />
          <Stack flex={1} spacing={1} sx={{ overflowY: "auto" }}>
            <BillingExplorerSavingOpportunitiesDrawerHeader
              onDrawerClose={onClose}
            />

            {loading ? (
              <LinearProgress />
            ) : (
              <BillingExplorerSavingOpportunitiesDrawerBody />
            )}
          </Stack>
        </Stack>
      </Stack>
    </Drawer>
  );
};

const paperProps = {
  sx: {
    width: "80dvw",
  },
};
const resizingDefaultParams = {
  defaultWidthPercent: 45,
  minWidthPercent: 30,
  key: "SelectWidgetDrawer",
};
